import React, { createContext, useContext, useEffect, useState } from "react";
import { io } from "socket.io-client";
import { sendRequest } from "../api";

import Error from "./Error";
import { useCategory } from "./Home/CategoryPublisherList";

const UserProcessingContext = createContext();
export const useUserContext = () => {
  const context = useContext(UserProcessingContext)
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProcessingContext')
  }
  return context
}

const defaultCounts = (publishers) => {
  return {
    categories: 0
  };
};

const UserProvider = ({ children }) => {
  const { fetchCategories } = useCategory()
  const [userData, setUserData] = useState(null);
  const [userSummaries, setUserSummaries] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null)
  const [counts, setCounts] = useState(defaultCounts([]));
  const [socket, setSocket] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isDisconnected, setIsDisconnected] = useState(false);

  useEffect(() => {
    reloadUser();
    reloadUserSummaries();
  }, []);

  useEffect(() => {
    if (socket) {
      const handleConnect = () => {
        setIsDisconnected(false);
      };
      
      const handleDisconnect = (reason) => {
        setIsDisconnected(true);
      };
      
      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);

      return () => {
        socket.off('connect', handleConnect);
        socket.off('disconnect', handleDisconnect);
      };
    }
  }, [socket]);

  useEffect(() => {
    if (counts && counts.user_summary > 0) {
      reloadUserSummaries()
      setCounts({ ...counts, user_summary: 0 });
    }
  }, [counts]);

  useEffect(() => {
    if (!socket && userData) {
      // In development, connect through webpack dev server proxy
      const websocketUrl = process.env.NODE_ENV === "development" 
        ? window.location.origin  // Use current origin (webpack dev server)
        : process.env.WEBSOCKET_URL;
      
      const socket = io(websocketUrl, {
        transports: ["polling", "websocket"], // Always try polling first in dev
        withCredentials: true,
        forceNew: true,
        timeout: 5000,
        transportOptions: {
          polling: {
            // extraHeaders: {
            //   'Authorization': token
            // }
          },
        },
      });
      
      socket.on('connect', () => {
        setIsDisconnected(false);
      });
      
      socket.on('connect_error', (error) => {
        console.error("❌ WebSocket connection error:", error);
        setIsDisconnected(true);
      });
      
      socket.on('disconnect', (reason) => {
        setIsDisconnected(true);
      });
      
      socket.on("message", handleEvent);
      
      // Set socket state after all event listeners are attached
      setSocket(socket);
      
      // Cleanup function
      return () => {
        if (socket) {
          socket.removeAllListeners();
          socket.disconnect();
        }
      };
    }
  }, [userData]);

  const reloadUser = async () => {
    try {
      const [user] = await sendRequest("/users");
      setUserData(user)
    } catch (error) {
      console.error("Error fetching user data:", error);
      setErrorMessage(error.message)
    }
  };

  const reloadUserSummaries = async () => {
    try {
      const userSummaries = await sendRequest("/user_summaries");
      setUserSummaries(userSummaries)
    } catch (error) {
      console.error("Error fetching user summaries:", error);
      setErrorMessage(error.message)
    }
  }

  const handleEvent = async (data) => {
    if (process.env.NODE_ENV === "development") {
      console.log("socket event", data);
    }

    if (data.startsWith("summaries_")) {
      const parts = data.split("_");
      const [key, count] = [parts.slice(0, -1).join("_"), parts.slice(-1)[0]];
      setCounts((prevCounts) => ({
        ...prevCounts,
        [key]: count,
      }));
    }

    if (data.startsWith("publisher_")) {
      const eventName = data.split("_").slice(0, -1).join("_");
      const newCount = data.split("_").slice(-1)[0];
      setCounts((prevCounts) => ({
        ...prevCounts,
        [eventName]: newCount,
      }));
    }

    if (data.startsWith(`user_summary_`)) {
      await reloadUserSummaries();
      setIsLoading(false)
    }

    if (data.startsWith("user_")) {
      const userId = data.split('_').slice(1)[0].toString()
      if (!userData || userId !== userData.id.toString()) {
        return;
      }

      const userEvent = data.split("_").slice(2).join("_");

      if (userEvent.startsWith("user_summary_progress")) {
        setIsLoading(true)
        setLoadingProgress(userEvent.split("_").slice(-1)[0])
      }

      if (userEvent.startsWith("categories")) {
        await fetchCategories()
        setIsLoading(false)
      }
    } else {
      console.log("Unknown socket event:", data)
      return
    }
  };

  const clearCounts = () => {
    setCounts(defaultCounts());
  };

  return (
    <UserProcessingContext.Provider
      value={{
        userData,
        user: userData, // Alias for backwards compatibility
        errorMessage,
        counts,
        clearCounts,
        reloadUser,
        setCounts,
        clearCounts,
        isLoading,
        setIsLoading,
        loadingProgress,
        userSummaries,
        socket,
        isDisconnected,
        setIsDisconnected,
      }}
    >
      <>
        {children}
      </>
    </UserProcessingContext.Provider>
  );
};

export default UserProvider;
